/**
 * Число в двоичную запись
 * ====================================
 */
import { Link, LoaderFunctionArgs, MetaFunction, useLoaderData, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import H2 from "~/components/H2";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolForm from "~/components/tool-page/ToolForm";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema, schemaOrg } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import { getTranslator } from "~/i18next.server";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import Author from "~/components/tool-page/Author";
import NumeralSystemsConverter, { NumeralSystem } from "~/components/NumeralSystemsConverter";
import { addNoindexMeta, ShouldIndexMeta } from "~/helpers/meta-noindex";
import { getBaseUrl } from "~/helpers/getBaseUrl";

const CATEGORY = "numbers";
const TKEY = "calculators.numbers.number_to_binary";
const ROUTE_ENDING = "number-to-binary";
const LAST_UPDATED = new Date("2026-03-26T00:00:00.000Z");

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  const meta = [
    { title: makeTitle(data?.meta.title) },
    { name: "description", content: data?.meta.description },
    createWebAppSchema(
      data?.meta.locale as string,
      data?.meta.title as string,
      [],
      LAST_UPDATED,
      schemaOrg.authorAlexFedorov
    )
  ];
  addNoindexMeta(meta, data.meta.shouldIndex);
  if (data.meta.canonical) {
    meta.push({ tagName: "link", rel: "canonical", href: data.meta.canonical });
  }
  return meta;
};

export const handle = {
  localisedLinks: ["en", "ru", "de", "fr", "es", "it", "pt"]
};

export const loader = async ({ request, params }: LoaderFunctionArgs) => {
  const number: string | null = params.number ?? null;

  const t = await getTranslator(request);

  const locale = getLocale(request.url);

  // Calculate result if number is set
  let error: string | null = null;
  let binaryNumber: string | null = null;

  try {
    if (number !== null) {
      const theNumber = parseInt(number, 10);
      if (!Number.isInteger(theNumber) || theNumber <= 0 || theNumber > 99999999) {
        error = t(`${TKEY}.error_invalid_number`);
      } else {
        binaryNumber = theNumber.toString(2);
      }

    }
  } catch (e) {
    error = t("error_unknown");
  }

  const title = number
    ? t(`${TKEY}.title_for_number`, { number })
    : t(`${TKEY}.title`)
  ;

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  if (number !== null) {
    breadcrumbs.push({ title, to: `${CATEGORY}/${ROUTE_ENDING}/${number}` });
  }

  const parsedNumber = number !== null ? parseInt(number, 10) : null;

  let shouldIndex: ShouldIndexMeta = {
    googlebot: number === null,
    yandex: false,
    bingbot: false,
  };

  if (
    number === null
    || (parsedNumber !== null && Number.isInteger(parsedNumber) && parsedNumber >= 1 && parsedNumber <= 1000)
  ) {
    shouldIndex.yandex = true;
    shouldIndex.bingbot = true;
  }

  return {
    number,
    binaryNumber: binaryNumber,
    error,
    meta: {
      locale,
      title,
      description: number !== null
        ? t(`${TKEY}.description_for_number`, { number, binaryNumber: binaryNumber })
        : t(`${TKEY}.description`),
      breadcrumbs,
      shouldIndex,
      canonical: getBaseUrl() + "/" + locale + "/" + CATEGORY + "/" + ROUTE_ENDING,
    }
  };
};

export default function Index() {
  const {
    meta,
    number,
    binaryNumber,
    error
  } = useLoaderData<typeof loader>();

  const { t } = useTranslation("translations");
  const locale = getLocale(useLocation().pathname);

  const commonBinaryNumbers = [
    1,
    2,
    3,
    4,
    5,
    6,
    7,
    8,
    9,
    10,
    50,
    100,
    500,
    1000,
    5000,
    10000,
    100000,
    1000000
  ];

  const steps = [];
  if (number) {
    let leftover = parseInt(number, 10);
    while (leftover > 0) {
      steps.push([leftover, Math.floor(leftover / 2), leftover % 2]);
      leftover = Math.floor(leftover / 2);
    }
  }

  return (
    <>
      <CalculatorLayout>

        <ToolHeaderNew
          name={meta.title}
          description={meta.description}
        />

        {binaryNumber && !error ? <Result color="positive">
          <H2 className="first-letter:capitalize">{t("answer")}:</H2>

          <p className="mt-8 text-3xl font-bold first-letter:capitalize font-serif">
            {binaryNumber}
          </p>
        </Result> : null}

        <ToolForm>
          <NumeralSystemsConverter
            from={NumeralSystem.decimal}
            to={NumeralSystem.binary}
            value={number ?? '1'}
          />
        </ToolForm>

        {error ?
          <Result color="negative">
            <p>{error}</p>
          </Result>
          : null}
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection>
            <H2>{t(`${TKEY}.info_h_binary_system`)}</H2>
            <p>{t(`${TKEY}.info_p_about_binary_system`)}</p>
          </AdditionalInfoSection>

          {number === null ? (
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert`)}</H2>
              <div dangerouslySetInnerHTML={
                { __html: t(`${TKEY}.info_p_how_to_convert`, { interpolation: { escapeValue: false } }) }
              } />
            </AdditionalInfoSection>
          ) : (
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_how_to_convert_number`, { number })}</H2>
              <p>{t(`${TKEY}.info_p_how_to_convert_number_explain1`)}</p>
              <ul>

                {steps.map(([currentNumber, result, remainder], i) => (
                  <li key={i}>
                    {t(`${TKEY}.info_p_how_to_convert_number_step`, {
                      number: currentNumber,
                      result,
                      remainder
                    })}
                  </li>
                ))}
              </ul>
              <p>{t(`${TKEY}.info_p_how_to_convert_number_explain2`, { number, result: binaryNumber })}</p>
            </AdditionalInfoSection>
          )}

          {number !== null ? null : (
            <AdditionalInfoSection>
              <H2>{t(`${TKEY}.info_h_common_numbers`)}</H2>
              <div className="columns-1 md:columns-3 gap-12">
                <table className="table-auto">
                  <tbody>
                  {commonBinaryNumbers.map((number) => (
                    <tr key={number} className="gap-2">
                      <td className="pr-3 text-right"><Link
                        to={`/${locale}/${CATEGORY}/${ROUTE_ENDING}/${number}`}>{number}</Link></td>
                      <td>{number.toString(2)}</td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            </AdditionalInfoSection>
          )}
        </AdditionalInfo>

        <Author lastUpdated={LAST_UPDATED} />
      </AdditionalContentLayout>
    </>
  );
}


